import { action, computed, makeObservable, observable } from 'mobx'
import RootStore from '@App/stores/root-store'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import * as searchAPI from '@App/api/routes/search'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { getNumTitles, INumTitles } from '@App/helpers/pagination/get-num-titles'
import { HasSearchCriteria } from '@App/stores/search/helpers/has-search-criteria'
import { findPageOnLimitChange } from '@App/helpers/pagination/find-page-on-limit-change'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { Works } from '@Entities/work/works'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { v4 as uuidv4 } from 'uuid'
import getWorkAutoCompleteSuggestions from '@App/helpers/search/get-work-autocomplete-suggestions'
import getWriterNameAutoCompleteSuggestions from '@App/helpers/search/get-writer-name-auto-complete-suggestions'
import getPublisherNameAutoCompleteSuggestions from '@App/helpers/search/get-publisher-name-auto-complete-suggestions'
import { createMatchedRecordingSearchOptions } from '@App/stores/search/work/matched-recording-search-options'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'

export const SEARCH_WORK_OPTIONS: Array<SelectOptionHide<WorkSearch>> = [
    {
        id: uuidv4(),
        value: 'combinedTitles',
        label: 'Work Title',
        isPrimary: true,
        autoCompleteFetcher: ({ searchTerm, rightsholderId, songwriterId, signal }) =>
            getWorkAutoCompleteSuggestions({
                property: 'combinedTitles',
                searchTerm,
                rightsholderId,
                songwriterId,
                signal,
            }),
        exactMatchOnly: true,
    },
    {
        id: uuidv4(),
        value: 'writerFullNames',
        label: 'Writer Name',
        autoCompleteFetcher: getWriterNameAutoCompleteSuggestions,
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'iswc', label: 'ISWC', isPrimary: true },
    { id: uuidv4(), value: 'hfaSongCode', label: 'MLC Song Code', isPrimary: true },
    { id: uuidv4(), value: 'writerNameNumbers', label: 'Writer IPI' },
    {
        id: uuidv4(),
        value: 'originalPubNames',
        label: 'Publisher Name',
        autoCompleteFetcher: getPublisherNameAutoCompleteSuggestions,
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'originalPubIpiNameNumbers', label: 'Publisher IPI' },
    { id: uuidv4(), value: 'bothPublisherNumbers', label: 'MLC Publisher Number' },
    { id: uuidv4(), value: 'matchedRecordingArtists', label: 'Artist' },
    ...createMatchedRecordingSearchOptions(),
]
export const DEFAULT_SEARCH_BY_WORK_CRITERIA: Array<SearchCriterion<WorkSearch>> = [
    {
        id: SEARCH_WORK_OPTIONS[0].id,
        searchTerm: '',
        isFixed: true,
        selectedOption: SEARCH_WORK_OPTIONS[0],
    },
]

export default class PublicSearchWorksStore implements HasSearchCriteria<WorkSearch> {
    createWorkSearchCriteriaStore(): SearchCriteriaStore<WorkSearch> {
        const workSearchMaxCriteria = 5
        return new SearchCriteriaStore(
            DEFAULT_SEARCH_BY_WORK_CRITERIA,
            SEARCH_WORK_OPTIONS,
            SearchOptionType.primary,
            RequiredCriteria.first,
            workSearchMaxCriteria
        )
    }

    private rootStore: RootStore

    private _publicSearchByWorkSearchQuery: WorkSearch

    private _publicSearchByWorkCriteriaWorks: Works | undefined

    private _publicSearchByWorkCriteriaPaginationInfo: PaginationInfo | undefined

    private _currentResultsPerPageLimit: number

    private _currentPage: number

    private _currentSearchCriteriaStore: SearchCriteriaStore<WorkSearch>

    private _redirectingToCatalog = false

    constructor(rootStore: RootStore) {
        makeObservable<
            PublicSearchWorksStore,
            | '_publicSearchByWorkCriteriaWorks'
            | '_publicSearchByWorkCriteriaPaginationInfo'
            | '_publicSearchByWorkSearchQuery'
            | '_currentPage'
            | '_currentResultsPerPageLimit'
            | '_currentSearchCriteriaStore'
        >(this, {
            _publicSearchByWorkCriteriaWorks: observable,
            _publicSearchByWorkCriteriaPaginationInfo: observable,
            _publicSearchByWorkSearchQuery: observable,
            _currentPage: observable,
            _currentResultsPerPageLimit: observable,
            _currentSearchCriteriaStore: observable,
            publicSearchByWorkSearchQuery: computed,
            numTitles: computed,
            setPublicSearchByWorkQuery: action,
            setRedirectingToCatalog: action,
            resetPublicSearchByWorkCriteriaIfNeeded: action,
            seedSearchFromUrl: action,
            resetSearchResults: action,
            setCurrentPage: action,
            onChangeLimit: action,
            applySearchResults: action,
        })

        this.rootStore = rootStore
        this.resetPublicSearchByWorkCriteriaIfNeeded()
    }

    get searchCriteriaStore(): SearchCriteriaStore<WorkSearch> {
        return this._currentSearchCriteriaStore
    }

    resetSearchCriteriaStore(): void {
        this._currentSearchCriteriaStore = this.createWorkSearchCriteriaStore()
    }

    get publicSearchByWorkSearchQuery(): Partial<WorkSearch> {
        return this._publicSearchByWorkSearchQuery
    }

    get publicSearchByWorkCriteriaWorks(): Works | undefined {
        return this._publicSearchByWorkCriteriaWorks
    }

    get publicSearchByWorkCriteriaPaginationInfo(): PaginationInfo | undefined {
        return this._publicSearchByWorkCriteriaPaginationInfo
    }

    get currentResultsPerPageLimit(): number {
        return this._currentResultsPerPageLimit
    }

    get currentPage(): number {
        return this._currentPage
    }

    get pagination(): PaginationBody {
        return {
            page: this._publicSearchByWorkCriteriaPaginationInfo?.currentPage ?? this._currentPage,
            limit: this._currentResultsPerPageLimit,
        }
    }

    setPublicSearchByWorkQuery(publicSearchByWorkSearchQuery: WorkSearch): void {
        this._publicSearchByWorkSearchQuery = publicSearchByWorkSearchQuery
    }

    get numTitles(): INumTitles | null {
        return this._publicSearchByWorkCriteriaPaginationInfo
            ? getNumTitles(
                  this._publicSearchByWorkCriteriaWorks?.count ?? 0,
                  this._publicSearchByWorkCriteriaPaginationInfo.currentPage,
                  this._currentResultsPerPageLimit
              )
            : null
    }

    /**
     * Performs the search request without touching any observable state, so it can be owned by an
     * external cache (TanStack Query). Errors propagate to the caller.
     */
    public async fetchWorks(query: Partial<WorkSearch>, pagination: PaginationBody): Promise<Works> {
        const apiResponse = await searchAPI.searchWorks(pagination, query)
        return { count: apiResponse.data.totalElements, works: apiResponse.data.content }
    }

    /** Stores results fetched elsewhere so components still reading the store stay in sync. */
    public applySearchResults(response: Works, pagination: PaginationBody): void {
        this._publicSearchByWorkCriteriaWorks = response
        if (pagination.limit && pagination.page) {
            this._publicSearchByWorkCriteriaPaginationInfo = calcNumPages(
                response.count,
                pagination.limit,
                pagination.page,
                pagination.refined
            )
        }
    }

    public reportSearchError(error: unknown): void {
        this.rootStore.popupStore.setAlertFromError(error)
    }

    /** Fetch + apply in one call, for callers outside TanStack Query (e.g. a bulk-action leave-page rerun). */
    async searchForWorks(
        pagination: PaginationBody = {
            page: this._currentPage,
            limit: this._currentResultsPerPageLimit,
        }
    ) {
        this.resetSearchResults()
        try {
            this.applySearchResults(await this.fetchWorks(this.publicSearchByWorkSearchQuery, pagination), pagination)
        } catch (e) {
            this.reportSearchError(e)
        }
    }

    public setCurrentPage(page: number) {
        this._currentPage = page
    }

    onChangeLimit(newLimit: number) {
        if (this._publicSearchByWorkCriteriaWorks) {
            const pagination: PaginationBody = { page: this._currentPage, limit: this._currentResultsPerPageLimit }
            const newPage = findPageOnLimitChange(pagination, this._publicSearchByWorkCriteriaWorks.count, newLimit, 1)
            this.setCurrentPage(newPage)
        }
        this._currentResultsPerPageLimit = newLimit
    }

    get redirectingToCatalog(): boolean {
        return this._redirectingToCatalog
    }

    setRedirectingToCatalog(value: boolean): void {
        this._redirectingToCatalog = value
    }

    /**
     * Starts a visit from a link's search terms. Run it before the tab first renders: it swaps in a criteria
     * store seeded from the URL, drops results from a different search, and — only if the terms satisfy the
     * form's own "ready to search" rule — makes them the search, so the first request already carries them.
     */
    seedSearchFromUrl(criteria: Record<string, unknown>): void {
        this.resetSearchCriteriaStore()
        this.searchCriteriaStore.applyCriteriaFromObject(criteria)
        this._currentPage = 1
        this.resetSearchResults()
        const rows = this.searchCriteriaStore.searchCriteria
        this._publicSearchByWorkSearchQuery = this.searchCriteriaStore.isCriteriaSearchReady(rows)
            ? mapCriteriaToObject(rows)
            : {}
    }

    resetPublicSearchByWorkCriteriaIfNeeded(): void {
        if (!this.redirectingToCatalog) {
            this._currentPage = 1
            this._currentResultsPerPageLimit = 10
            this.resetSearchCriteriaStore()
            this._publicSearchByWorkSearchQuery = {}
            this.resetSearchResults()
        }
        this.setRedirectingToCatalog(false)
    }

    /** Clears prior results so a new search never shows the previous criteria's results, success or not. */
    public resetSearchResults(): void {
        this._publicSearchByWorkCriteriaWorks = undefined
        this._publicSearchByWorkCriteriaPaginationInfo = undefined
    }
}
